import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Chip,
  Divider,
  Paper,
  Grid,
  Alert,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

const normalizeTargetSchema = (targetSchema) => {
  if (!targetSchema) return [];
  if (Array.isArray(targetSchema)) return targetSchema;
  return [{ tableName: targetSchema, columns: [], id: targetSchema }];
};

// Helper function to extract table and column from "table.column" format
const parseSourceColumn = (sourceColumn) => {
  if (!sourceColumn) return { table: '', column: '' };
  const parts = sourceColumn.split('.');
  if (parts.length === 2) {
    return { table: parts[0], column: parts[1] };
  }
  return { table: '', column: sourceColumn };
};

const ComputeMappingDialog = ({
  open,
  onClose,
  sources = [],
  targetSchema,
  fetchedHeader = [],
  initialValues = null,
  existingMappings = [],
  onSave,
  reviewMode = false,
  onReject,
  onApprove,
}) => {
  const targets = useMemo(() => normalizeTargetSchema(targetSchema), [targetSchema]);

  // State for compute mapping
  const [sourceTable, setSourceTable] = useState('');
  const [primaryColumn, setPrimaryColumn] = useState('');
  const [secondaryColumn, setSecondaryColumn] = useState('');
  const [targetTable, setTargetTable] = useState('');
  const [targetColumn, setTargetColumn] = useState('');

  // Parse initial values when dialog opens
  useEffect(() => {
    if (open && initialValues) {
      console.log("Initial values for edit:", initialValues);

      // Set source table
      if (initialValues.sourceTable) {
        setSourceTable(initialValues.sourceTable);
      } else if (initialValues.left) {
        setSourceTable(initialValues.left);
      }

      // Set primary column
      if (initialValues.primaryColumn) {
        setPrimaryColumn(initialValues.primaryColumn);
      } else if (initialValues.leftKey) {
        setPrimaryColumn(initialValues.leftKey);
      } else if (initialValues.source_columns && initialValues.source_columns.length > 0) {
        const parsed = parseSourceColumn(initialValues.source_columns[0]);
        setPrimaryColumn(parsed.column);
      }

      // Set secondary column (if exists)
      if (initialValues.secondaryColumn) {
        setSecondaryColumn(initialValues.secondaryColumn);
      } else if (initialValues.source_columns && initialValues.source_columns.length > 1) {
        const parsed = parseSourceColumn(initialValues.source_columns[1]);
        setSecondaryColumn(parsed.column);
      }

      // Set target table
      if (initialValues.destinationName) {
        setTargetTable(initialValues.destinationName);
      } else if (initialValues.destination_name) {
        setTargetTable(initialValues.destination_name);
      } else if (initialValues.right) {
        setTargetTable(initialValues.right);
      }

      // Set target column
      if (initialValues.targetColumn) {
        setTargetColumn(initialValues.targetColumn);
      } else if (initialValues.target_column) {
        setTargetColumn(initialValues.target_column);
      } else if (initialValues.rightKey) {
        setTargetColumn(initialValues.rightKey);
      }
    } else if (open && !initialValues) {
      // Reset for new mapping
      setSourceTable('');
      setPrimaryColumn('');
      setSecondaryColumn('');
      setTargetTable('');
      setTargetColumn('');
    }
  }, [open, initialValues]);

  // Source table options
  const sourceTableOptions = useMemo(
    () => sources.map((s) => ({ value: s.name || s.id, label: s.name || s.id })),
    [sources]
  );

  // Available columns for the selected table
  const availableColumns = useMemo(() => {
    if (!sourceTable) return [];
    const src = sources.find((s) => (s.name || s.id) === sourceTable);
    const cols = src?.columns || [];
    return Array.isArray(cols) ? cols.map((c) => ({ value: c, label: c })) : [];
  }, [sources, sourceTable]);

  // Get columns for primary select (exclude secondary if selected)
  const primaryColumnOptions = useMemo(() => {
    return availableColumns.filter(col => col.value !== secondaryColumn);
  }, [availableColumns, secondaryColumn]);

  // Get columns for secondary select (exclude primary if selected)
  const secondaryColumnOptions = useMemo(() => {
    return availableColumns.filter(col => col.value !== primaryColumn);
  }, [availableColumns, primaryColumn]);

  // Target table options
  const targetTableOptions = useMemo(
    () => targets.map((t) => ({ value: t.tableName || t.id, label: t.tableName || t.id })),
    [targets]
  );

  const mappedTargetColsForTable = useMemo(() => {
    if (!targetTable || !Array.isArray(existingMappings)) return new Set();
    return new Set(
      existingMappings
        .filter((m) => (m.destination_name || m.destinationName) === targetTable)
        .map((m) => (m.target_column || m.targetColumn || '').trim())
        .filter(Boolean)
    );
  }, [targetTable, existingMappings]);

  // Target column options (exclude already-mapped columns; keep current selection when editing)
  const targetColumnOptions = useMemo(() => {
    if (!targetTable) return [];
    const target = targets.find((t) => (t.tableName || t.id) === targetTable);
    const normalizeCol = (c) => {
      if (c == null) return '';
      if (typeof c === 'string') return c;
      return c.column_name ?? c.name ?? c.columnName ?? '';
    };
    let opts = [];
    if (target?.columns?.length) {
      opts = target.columns
        .map((c) => {
          const name = normalizeCol(c);
          return { value: name, label: name };
        })
        .filter((opt) => opt.value != null && String(opt.value).trim() !== '');
    }
    if (opts.length === 0) {
      opts = (fetchedHeader || [])
        .filter((h) => h.sourceTable === targetTable)
        .map((h) => ({ value: h.columnName ?? h.column_name, label: h.columnName ?? h.column_name }))
        .filter((opt) => opt.value != null && opt.value !== '');
    }
    const currentSelection = initialValues?.targetColumn ?? initialValues?.target_column ?? targetColumn;
    return opts.filter(
      (opt) => !mappedTargetColsForTable.has(opt.value) || opt.value === currentSelection
    );
  }, [targets, targetTable, fetchedHeader, mappedTargetColsForTable, initialValues?.targetColumn, initialValues?.target_column, targetColumn]);

  const handleClose = () => {
    setSourceTable('');
    setPrimaryColumn('');
    setSecondaryColumn('');
    setTargetTable('');
    setTargetColumn('');
    onClose();
  };

  const handleSave = () => {
    if (!sourceTable || !primaryColumn || !targetTable || !targetColumn) {
      return;
    }

    // Build source columns array
    const sourceCols = [primaryColumn];
    if (secondaryColumn) {
      sourceCols.push(secondaryColumn);
    }

    // Format source columns with table prefix
    const formattedSourceColumns = sourceCols.map(col => `${sourceTable}.${col}`);

    // Prepare the payload
    const payload = {
      left: sourceTable,
      right: targetTable,
      leftKey: primaryColumn,
      rightKey: targetColumn,
      targetColumn,
      destinationName: targetTable,
      sourceColumns: sourceCols,
      primaryColumn,
      secondaryColumn: secondaryColumn || null,
      mapping_type: 'COMPUTE',
      mappingType: 'COMPUTE',
      source_columns: formattedSourceColumns,
    };

    // Include original metadata if editing
    if (initialValues) {
      if (initialValues.confidence) payload.confidence = initialValues.confidence;
      if (initialValues.mapping_state) payload.mapping_state = initialValues.mapping_state;
      if (initialValues.mappingState) payload.mappingState = initialValues.mappingState;
      if (initialValues.possible_matches) payload.possible_matches = initialValues.possible_matches;
      if (initialValues.possibleMatches) payload.possibleMatches = initialValues.possibleMatches;
      if (initialValues.reasoning) payload.reasoning = initialValues.reasoning;
      if (initialValues.id) payload.id = initialValues.id;
    }

    onSave?.(payload);
    handleClose();
  };

  const canSave = sourceTable && primaryColumn && targetTable && targetColumn;

  const selectSx = {
    minWidth: 160,
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: '#fafafa',
      '&:hover': { backgroundColor: '#f5f5f5' },
      '&.Mui-focused': { backgroundColor: '#fff' },
    },
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 24px 48px rgba(0,0,0,0.12)',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          fontSize: '1.25rem',
          color: '#1f2937',
          borderBottom: '1px solid #e5e7eb',
          py: 2,
          px: 3,
          backgroundColor: '#fafafa',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span>{initialValues ? 'Edit Compute Mapping' : 'New Compute Mapping'}</span>
        {initialValues?.mapping_state && (
          <Chip
            label={initialValues.mapping_state.replace('_', ' ').toUpperCase()}
            size="small"
            sx={{
              backgroundColor:
                initialValues.mapping_state === 'auto_merge' ? '#e3f2fd' :
                  initialValues.mapping_state === 'pending' ? '#fff3e0' : '#f5f5f5',
              color:
                initialValues.mapping_state === 'auto_merge' ? '#1976d2' :
                  initialValues.mapping_state === 'pending' ? '#ed6c02' : '#1f2937',
              fontWeight: 600,
              borderRadius: 1.5,
            }}
          />
        )}
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 3, backgroundColor: '#fff' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

          {/* Info Alert - LLM Generated */}
          <Alert
            severity="info"
            icon={<InfoIcon />}
            sx={{
              borderRadius: 2,
              backgroundColor: '#e3f2fd',
              '& .MuiAlert-message': { color: '#035c8f' }
            }}
          >
            <Typography variant="body2">
              <strong>LLM Generated Mapping:</strong> This computation operation was automatically suggested by AI.
              You can review and adjust the source columns below.
            </Typography>
          </Alert>

          {/* Source Section */}
          <Box>
            <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.08em', fontSize: '0.7rem', display: 'block', mb: 1.5 }}>
              SOURCE
            </Typography>

            {/* Source Table Selection */}
            <Box sx={{ mb: 3 }}>
              <FormControl size="small" fullWidth sx={selectSx}>
                <InputLabel id="compute-source-table">Source table</InputLabel>
                <Select
                  labelId="compute-source-table"
                  value={sourceTable}
                  label="Source table"
                  onChange={(e) => {
                    setSourceTable(e.target.value);
                    setPrimaryColumn('');
                    setSecondaryColumn('');
                  }}
                  disabled={reviewMode}
                  MenuProps={{ PaperProps: { sx: { borderRadius: 2, mt: 1.5 } } }}
                >
                  {sourceTableOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Source Columns Selection - Two Side by Side Dropdowns */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1f2937', mb: 2 }}>
                Select Source Columns <Typography component="span" variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>(Max 2 columns)</Typography>
              </Typography>

              <Grid container spacing={2}>
                {/* Primary Column - Required */}
                <Grid item xs={12} md={6}>
                  <FormControl size="small" fullWidth sx={selectSx}>
                    <InputLabel id="primary-column">Column 1 *</InputLabel>
                    <Select
                      labelId="primary-column"
                      value={primaryColumn}
                      label="Column 1 *"
                      onChange={(e) => setPrimaryColumn(e.target.value)}
                      disabled={reviewMode || !sourceTable}
                      MenuProps={{ PaperProps: { sx: { borderRadius: 2, mt: 1.5, maxHeight: 300 } } }}
                    >
                      {primaryColumnOptions.map((col) => (
                        <MenuItem key={col.value} value={col.value}>
                          {col.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, ml: 1 }}>
                      Primary column (required)
                    </Typography>
                  </FormControl>
                </Grid>

                {/* Secondary Column - Optional */}
                <Grid item xs={12} md={6}>
                  <FormControl size="small" fullWidth sx={selectSx}>
                    <InputLabel id="secondary-column">Column 2 (Optional)</InputLabel>
                    <Select
                      labelId="secondary-column"
                      value={secondaryColumn}
                      label="Column 2 (Optional)"
                      onChange={(e) => setSecondaryColumn(e.target.value)}
                      disabled={reviewMode || !sourceTable}
                      MenuProps={{ PaperProps: { sx: { borderRadius: 2, mt: 1.5, maxHeight: 300 } } }}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {secondaryColumnOptions.map((col) => (
                        <MenuItem key={col.value} value={col.value}>
                          {col.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, ml: 1 }}>
                      Additional column (optional)
                    </Typography>
                  </FormControl>
                </Grid>
              </Grid>

              {/* {primaryColumn && secondaryColumn && (
                <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }} icon={<InfoIcon />}>
                  <Typography variant="caption">
                    Two columns selected. The computation will use both columns.
                  </Typography>
                </Alert>
              )} */}
            </Box>
          </Box>

          {/* Divider */}
          <Divider sx={{ my: 1 }}>
            <Chip
              label="MAP TO TARGET"
              size="small"
              sx={{
                borderRadius: 1.5,
                backgroundColor: '#f0f0f0',
                color: '#1f2937',
                fontSize: '0.7rem',
                fontWeight: 600
              }}
            />
          </Divider>

          {/* Target Section */}
          <Box>
            <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.08em', fontSize: '0.7rem', display: 'block', mb: 1.5 }}>
              TARGET
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl size="small" fullWidth sx={selectSx} disabled={reviewMode}>
                <InputLabel id="compute-target-table">Target table</InputLabel>
                <Select
                  labelId="compute-target-table"
                  value={targetTable}
                  label="Target table"
                  onChange={(e) => {
                    setTargetTable(e.target.value);
                    setTargetColumn('');
                  }}
                  disabled={reviewMode}
                  MenuProps={{ PaperProps: { sx: { borderRadius: 2, mt: 1.5 } } }}
                >
                  {targetTableOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth sx={selectSx} disabled={reviewMode || !targetTable}>
                <InputLabel id="compute-target-col">Target column</InputLabel>
                <Select
                  labelId="compute-target-col"
                  value={targetColumn ?? ''}
                  label="Target column"
                  onChange={(e) => setTargetColumn(e.target.value)}
                  disabled={reviewMode || !targetTable}
                  MenuProps={{
                    PaperProps: { sx: { borderRadius: 2, mt: 1.5 } },
                  }}
                >
                  {targetTable && targetColumnOptions.length === 0 ? (
                    <MenuItem value="" disabled>
                      No columns available
                    </MenuItem>
                  ) : (
                    targetColumnOptions.map((opt, idx) => (
                      <MenuItem key={`targetcol-${idx}-${opt.value}`} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Preview Section */}
          {canSave && (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mt: 2,
                borderRadius: 2,
                backgroundColor: '#f9f9f9',
                border: '1px dashed #e0e0e0'
              }}
            >
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', mb: 1 }}>
                COMPUTE MAPPING PREVIEW
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={`FROM: ${sourceTable}`}
                  size="small"
                  sx={{ backgroundColor: '#e3f2fd', borderRadius: 1.5 }}
                />
                <Typography variant="body2" sx={{ color: '#1f2937' }}>→</Typography>

                {/* Preview chips */}
                <Chip
                  label={primaryColumn}
                  size="small"
                  sx={{
                    backgroundColor: '#00043c',
                    color: '#fff',
                    borderRadius: 1.5,
                    fontWeight: 500,
                  }}
                />

                {secondaryColumn && (
                  <>
                    <Typography variant="body2" sx={{ color: '#1f2937' }}>+</Typography>
                    <Chip
                      label={secondaryColumn}
                      size="small"
                      sx={{
                        backgroundColor: '#5e35b1',
                        color: '#fff',
                        borderRadius: 1.5,
                        fontWeight: 500,
                      }}
                    />
                  </>
                )}

                <Typography variant="body2" sx={{ color: '#1f2937' }}>→</Typography>
                <Chip
                  label={`TO: ${targetTable}.${targetColumn}`}
                  size="small"
                  sx={{ backgroundColor: '#ff9800', color: '#fff', borderRadius: 1.5 }}
                />
              </Box>

              {/* Reasoning Display */}
              {initialValues?.reasoning && (
                <Box sx={{ mt: 1.5, p: 1.5, backgroundColor: '#fff', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                    AI Reasoning:
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1f2937' }}>
                    {initialValues.reasoning}
                  </Typography>
                </Box>
              )}
            </Paper>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1.5,
          borderTop: '1px solid #e5e7eb',
          backgroundColor: '#fafafa',
        }}
      >
        {reviewMode ? (
          <>
            <Button onClick={onReject} variant="contained" sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 3, bgcolor: '#dc2626', color: '#fff', '&:hover': { bgcolor: '#b91c1c' } }}>
              Reject
            </Button>
            <Button onClick={onApprove} variant="contained" sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 3, bgcolor: '#16a34a', color: '#fff', '&:hover': { bgcolor: '#15803d' } }}>
              Approve
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleClose} variant="outlined" color="inherit" sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 2.5 }}>
              Cancel
            </Button>
            <Button onClick={handleSave} variant="contained" disabled={!canSave} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 3, bgcolor: '#00043c', color: '#fff', boxShadow: '0 2px 8px rgba(0, 4, 60, 0.35)', '&:hover': { bgcolor: '#000335', boxShadow: '0 4px 12px rgba(0, 4, 60, 0.4)' } }}>
              {initialValues ? 'Update' : 'Save'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ComputeMappingDialog;